import { generatePath, matchPath } from 'react-router';

import { put, call, fork, select, take, all, takeEvery } from 'redux-saga/effects';
import { validate } from 'uuid';

import * as type from 'constants/ActionTypes';
import { PATH } from 'constants/RoutesConstants';
import { scopeToPage } from 'modules/app/sagas/utils';
import { cleanChecklistsSelect } from 'modules/checklists/actions/dashboard';
import { resetDocuments } from 'modules/config/actions/general';
import { setDealerConfigReady } from 'modules/dealerConfiguration/actions';
import { fetchDealer, selectDealer } from 'modules/dealers/actions';
import { getAvailableDealerIds, selectFirstAvailableDealer } from 'modules/dealers/dealerSagas';
import { selectGroup } from 'modules/groups/actions';
import { FETCH_GROUP_SUCCESS, FETCH_GROUP_FAILURE } from 'modules/groups/actionTypes';
import { getGroupById, getSelectedGroupId } from 'modules/groups/selectors';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { navigate } from 'modules/router/navigate';
import { getLocation } from 'modules/router/selectors';
import { getSelectedDealerId } from 'selectors/dealerSelectors';

import tableFnDictionary from './tabs.bootloader';
import administrationSaga from '../administration/sagas';
import integrationsSaga from '../integrations/sagas';
import salesSaga from '../sales/sagas';

function* preloadDealerConfig(dealerId) {
  const params = { projection: 'config' };
  const selectedDealerId = yield select(getSelectedDealerId);
  const action = selectedDealerId === dealerId ? fetchDealer : selectDealer;

  yield put(action(dealerId, params));
}

function* redirectToValidDealer(tabKey = 'general') {
  const location = yield select(getLocation);
  const [newDealerId] = yield call(getAvailableDealerIds, location.pathname);
  const redirectParams = { dealerId: newDealerId, tabKey };
  const redirectTo = generatePath(PATH.DEALER_CONFIG_PAGE.path, redirectParams);
  yield call(navigate, redirectTo);
}

// THE WHOLE FORCERELOAD is to remove when MIGRATING checklist to the new assignments !
function* selectDealerWaiter(forceGroupReload = false) {
  const location = yield select(getLocation);
  const groupId = yield select(getSelectedGroupId);
  const { error, payload: dealer } = yield take([type.FETCH_DEALER_SUCCESS, type.FETCH_DEALER_FAILURE]);

  if (error) {
    return yield call(selectFirstAvailableDealer, location.pathname);
  }

  const dealerGroupId = dealer.group?.id;
  const alreadyExistingGroup = yield select(state => getGroupById(state, dealerGroupId));
  if (dealerGroupId && (forceGroupReload || (groupId !== dealerGroupId && !alreadyExistingGroup))) {
    // to remove when migration of checklist assignments, please :D
    if (forceGroupReload) {
      yield put(selectGroup(dealerGroupId, { projection: 'config' }, { forceReload: true }));
    }
    const { error: groupFetchingFailed } = yield take([FETCH_GROUP_SUCCESS, FETCH_GROUP_FAILURE]);
    return groupFetchingFailed;
  }
  return false;
}

function* getCurrentActiveTabKey() {
  const location = yield select(getLocation);
  const match = matchPath(PATH.DEALER_CONFIG_PAGE.path, location.pathname);
  return match?.params?.tabKey ?? 'general';
}

/**
 * Initializes a dealer configuration tab with lazy loading (called on tab navigation).
 *
 * This is the core function of the lazy loading architecture. It's called whenever:
 * - A user navigates to a new tab (LOCATION_CHANGE action)
 * - The dealer configuration page is first loaded
 *
 * **Flow:**
 * 1. Extracts tab key from URL (e.g., 'sales', 'parts', 'general')
 * 2. Checks if tab has already been initialized (via visitedTabsSet)
 * 3. If not visited, looks up tab's initializer in tableFnDictionary
 * 4. Calls the initializer to load tab-specific data
 * 5. Marks tab as visited to prevent redundant loads
 *
 * **Performance optimization:**
 * - Tabs are initialized only once per dealer configuration page session
 * - visitedTabsSet prevents duplicate API calls when navigating between tabs
 * - Data is loaded on-demand, not upfront
 *
 * **Lifecycle:**
 * - visitedTabsSet is scoped to a single page session via scopeToPage
 * - When dealer changes or user leaves the page, the Set is reset via saga lifecycle
 * - New dealer selection triggers a fresh initialization with empty visitedTabsSet
 *
 * @generator
 * @param {Set<string>} visitedTabsSet - Set tracking which tabs have been initialized in current session.
 *                                       Mutated by this function (tab key added after init).
 *                                       Scoped to current dealer via saga lifecycle management.
 * @yields {Effect} Redux selects and calls to tab-specific initializers
 */
function* initializeDealerTab(visitedTabsSet) {
  const tabKey = yield call(getCurrentActiveTabKey);

  if (!visitedTabsSet.has(tabKey)) {
    const haveInitializer = typeof tableFnDictionary[tabKey] === 'function';
    if (haveInitializer) {
      yield call(tableFnDictionary[tabKey], visitedTabsSet);
    }
    visitedTabsSet.add(tabKey);
  }
}

function* initializeDealerConfigPage(visitedTabsSet) {
  const location = yield select(getLocation);
  const match = matchPath(PATH.DEALER_CONFIG_PAGE.path, location.pathname);
  const { dealerId, tabKey = 'general' } = match?.params ?? {};

  if (validate(dealerId)) {
    yield call(preloadDealerConfig, dealerId);
    const error = yield call(selectDealerWaiter, true);
    if (!error) {
      yield all([put(resetDocuments()), put(cleanChecklistsSelect())]);

      yield call(initializeDealerTab, visitedTabsSet);
      yield put(setDealerConfigReady(true));
    }
  } else {
    yield call(redirectToValidDealer, tabKey);
  }
}

function* redirectToDealerPage({ payload: { dealerId } }) {
  yield all([put(setDealerConfigReady(false)), call(selectDealerWaiter)]);
  const location = yield select(getLocation);
  const match = matchPath(PATH.DEALER_CONFIG_PAGE.path, location.pathname);
  const redirectTo = generatePath(PATH.DEALER_CONFIG_PAGE.path, { dealerId, tabKey: match?.params?.tabKey ?? 'general' });
  yield call(navigate, redirectTo);
}

function* onDealerConfigWorker() {
  const visitedTabsSet = new Set();
  yield call(initializeDealerConfigPage, visitedTabsSet);
  yield takeEvery(type.SELECT_DEALER, redirectToDealerPage);
  yield takeEvery(LOCATION_CHANGE, initializeDealerTab, visitedTabsSet);
}

function* onLeaveDealerConfig() {
  yield put(setDealerConfigReady(false));
}

const channelPattern = ({ type: actionType, payload }) =>
  actionType === LOCATION_CHANGE && payload.action !== 'REPLACE';

export default function* saga() {
  yield fork(salesSaga);
  yield fork(administrationSaga);
  yield fork(integrationsSaga);
  yield scopeToPage([PATH.DEALER_CONFIG_PAGE], onDealerConfigWorker, onLeaveDealerConfig, channelPattern);
}
